import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const TESTIMONIALS = [
  {
    quote: 'A consultoria da Metodologia Justi transformou a forma como nosso escritório entrega projetos. Padronizamos famílias, templates e fluxos no Revit — hoje entregamos mais rápido e com muito menos retrabalho.',
    name: 'Arq. Marina Albuquerque',
    role: 'Arquiteta',
    image: 'https://static.prod-images.emergentagent.com/jobs/2b41e164-3d6f-488e-a974-c4593600dbb3/images/8651f8b7fa13317dd8c68f7bf0e5524a424e28e6094714148e287a0da22e427d.jpeg',
  },
  {
    quote: 'Implantar o BIM na nossa construtora parecia impossível até chamarmos a equipe do Alex Justi. O acompanhamento foi próximo, prático e focado em resultado. Ganhamos previsibilidade dentro e fora da obra.',
    name: 'Eng. Rafael Nogueira',
    role: 'Engenheiro Civil',
    image: 'https://static.prod-images.emergentagent.com/jobs/2b41e164-3d6f-488e-a974-c4593600dbb3/images/f5f64f71128b2a5a54b88f5256607e1547db1a9c600684f555d69691038aec73.jpeg',
  },
  {
    quote: 'O diagnóstico BIM apontou exatamente onde estávamos perdendo tempo. Em poucos meses de consultoria, a compatibilização de projetos deixou de ser um problema na nossa equipe.',
    name: 'Eng. Patrícia Fontes',
    role: 'Engenheira',
    image: 'https://static.prod-images.emergentagent.com/jobs/2b41e164-3d6f-488e-a974-c4593600dbb3/images/967d7dd84245206b0bed50a12865df85e585361dd726b14417b4931dddda92eb.jpeg',
  },
  {
    quote: 'Comecei a Formação Revit do zero e hoje modelo projetos completos com segurança. As aulas são diretas ao ponto e o suporte faz toda a diferença. Consegui meu primeiro estágio graças a isso.',
    name: 'Lucas Ferreira',
    role: 'Estudante de Arquitetura',
    image: 'https://static.prod-images.emergentagent.com/jobs/2b41e164-3d6f-488e-a974-c4593600dbb3/images/17d17b713348af692dfd0e001e5c4444a0bde66ab37c9a7fbf343fe2aa550e22.jpeg',
  },
  {
    quote: 'A formação me deu a base que a faculdade não deu em BIM. Saí modelando estrutura e compatibilizando com arquitetura. Recomendo para todo estudante que quer se destacar no mercado.',
    name: 'Camila Rocha',
    role: 'Estudante de Engenharia Civil',
    image: 'https://static.prod-images.emergentagent.com/jobs/2b41e164-3d6f-488e-a974-c4593600dbb3/images/936f4dbcd3119ba9a33792199f913ba9ee9832bfef0e6a53fa06d9b8e3e69a31.jpeg',
  },
  {
    quote: 'Os treinamentos são muito bem estruturados, do básico ao avançado. Aprendi Revit de verdade, com projetos reais. Valeu cada minuto — hoje me sinto preparado para o mercado.',
    name: 'Bruno Tavares',
    role: 'Estudante de Arquitetura',
    image: 'https://static.prod-images.emergentagent.com/jobs/2b41e164-3d6f-488e-a974-c4593600dbb3/images/0f1dca58c7c68591097cb9c589a2d57984a3dc97b64e9df51a57f2e92239425c.jpeg',
  },
];

const CARD_BG = '#26262b';
const ROTATE_MS = 7000;

const TestimonialsSection = () => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const count = TESTIMONIALS.length;

  const goTo = useCallback((i) => setIndex(((i % count) + count) % count), [count]);
  const next = useCallback(() => goTo(index + 1), [goTo, index]);
  const prev = useCallback(() => goTo(index - 1), [goTo, index]);

  useEffect(() => {
    if (paused) return undefined;
    const t = setInterval(() => setIndex((i) => (i + 1) % count), ROTATE_MS);
    return () => clearInterval(t);
  }, [paused, count]);

  const t = TESTIMONIALS[index];

  return (
    <section className="bg-black py-8 md:py-10" data-testid="testimonials-section">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="text-center mb-10">
          <span className="inline-block text-orange-500 text-sm font-semibold tracking-[0.2em] uppercase mb-3">Depoimentos</span>
          <h2 className="text-3xl md:text-4xl font-bold text-white">
            Quem já viveu a <span className="text-red-500">Metodologia Justi</span>
          </h2>
        </div>

        <div
          className="relative max-w-5xl mx-auto"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <div
            className="relative rounded-2xl border border-zinc-800 overflow-hidden shadow-xl"
            style={{ backgroundColor: CARD_BG }}
          >
            {/* Foto da pessoa */}
            <div className="absolute right-0 top-0 bottom-0 w-[42%] sm:w-[34%] md:w-[30%] hidden sm:block">
              <img
                key={t.image}
                src={t.image}
                alt={t.name}
                className="w-full h-full object-cover object-top animate-in fade-in duration-700"
              />
              <div
                className="absolute inset-0"
                style={{ background: `linear-gradient(to right, ${CARD_BG} 0%, ${CARD_BG}cc 30%, transparent 70%)` }}
              />
            </div>

            {/* Conteúdo */}
            <div
              key={index}
              className="relative p-7 md:p-12 sm:pr-[34%] md:pr-[30%] min-h-[260px] flex flex-col justify-center animate-in fade-in slide-in-from-bottom-2 duration-500"
            >
              <div className="mb-6">
                <span className="inline-block border-2 border-orange-500 text-orange-500 rounded-full px-6 py-2 text-xs font-bold tracking-[0.15em] uppercase">
                  Depoimento
                </span>
              </div>
              <p className="text-gray-100 text-base md:text-xl italic leading-relaxed mb-6" data-testid="testimonial-quote">
                “{t.quote}”
              </p>
              <div className="text-sm md:text-base">
                <span className="font-bold text-white">{t.name}</span>
                <span className="text-gray-400">, {t.role}</span>
              </div>
            </div>
          </div>

          {/* Controles */}
          <div className="flex items-center justify-center gap-4 mt-6">
            <button
              type="button"
              onClick={prev}
              aria-label="Depoimento anterior"
              data-testid="testimonial-prev"
              className="w-10 h-10 rounded-full border border-zinc-700 text-gray-300 hover:text-white hover:border-orange-500 flex items-center justify-center transition-colors"
            >
              <ChevronLeft size={20} />
            </button>

            <div className="flex items-center gap-2">
              {TESTIMONIALS.map((item, i) => (
                <button
                  key={item.name}
                  type="button"
                  onClick={() => goTo(i)}
                  aria-label={`Ir para depoimento ${i + 1}`}
                  data-testid={`testimonial-dot-${i}`}
                  className={`h-2 rounded-full transition-all ${i === index ? 'w-7 bg-orange-500' : 'w-2 bg-zinc-600 hover:bg-zinc-500'}`}
                />
              ))}
            </div>

            <button
              type="button"
              onClick={next}
              aria-label="Próximo depoimento"
              data-testid="testimonial-next"
              className="w-10 h-10 rounded-full border border-zinc-700 text-gray-300 hover:text-white hover:border-orange-500 flex items-center justify-center transition-colors"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
